'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/browser';

/**
 * Global error handler component to catch unhandled promise rejections
 * and other global JavaScript errors that might not be caught by React.
 */
export function GlobalErrorHandler() {
  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);

      // Extract more detailed error information
      const error = event.reason;
      let errorDetails = {
        message: 'Unhandled promise rejection',
        reason: error,
      };

      // If the error is an object with code/message (like in the Sentry report)
      if (error && typeof error === 'object') {
        errorDetails = {
          message: error.message || 'Unhandled promise rejection',
          reason: error,
        };

        // Add specific handling for common error patterns
        if (error.code) {
          errorDetails.message = `Error ${error.code}: ${error.message || 'Unknown error'}`;
        }
      }

      // Report to Sentry with additional context
      Sentry.captureException(new Error(errorDetails.message), {
        tags: {
          errorType: 'unhandledRejection',
          errorSource: 'globalHandler',
        },
        extra: {
          originalReason: errorDetails.reason,
          promiseRejectionEvent: {
            type: event.type,
            reason: event.reason,
          },
        },
        contexts: {
          promiseRejection: {
            handled: false,
            source: 'window.onunhandledrejection',
          },
        },
      });

      // Prevent the default browser console error for better UX
      // but don't call preventDefault() to ensure proper error reporting
    };

    // Handle other global errors that might escape React
    const handleGlobalError = (event: ErrorEvent) => {
      console.error('Global JavaScript error:', event.error || event.message);

      // Let Sentry's automatic error capture handle this, but add context
      if (event.error) {
        Sentry.withScope((scope) => {
          scope.setTag('errorSource', 'globalHandler');
          scope.setContext('errorEvent', {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            message: event.message,
          });
          Sentry.captureException(event.error);
        });
      }
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleGlobalError);

    // Cleanup function
    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
      window.removeEventListener('error', handleGlobalError);
    };
  }, []);

  // This component doesn't render anything
  return null;
}
